'use client';

import { resolvePaletteOrMuiButtonColor } from '@/ui/ComponentLibrary/Buttons/utils';
import { colorPaletteOptions } from '@/ui/theme/colorPalette';
import { hubThemeLight } from '@/ui/theme/storybook-theme';
import { CustomThemeProvider } from '@/ui/theme/ThemeContext';
import Button, { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';

export interface MinimalButtonProps
  extends Pick<
    ButtonProps,
    | 'variant'
    | 'startIcon'
    | 'endIcon'
    | 'disabled'
    | 'onClick'
    | 'type'
    | 'size'
    | 'fullWidth'
  > {
  text: string;
  loading?: boolean;
  color?: (typeof colorPaletteOptions)[number];
  expandHeight?: boolean;
  tooltip?: string;
}

/** Use white text for contained buttons when background is a palette shade (no contrastText in colorPalette). */
function getContrastText(_hex: string): string {
  return '#fff';
}

const MinimalButton: React.FC<MinimalButtonProps> = ({
  color = 'primary',
  variant = 'contained',
  text,
  startIcon,
  endIcon,
  loading = false,
  disabled = false,
  onClick,
  type,
  size,
  fullWidth = false,
  expandHeight = false,
  tooltip,
}) => {
  const { resolvedColor, muiColor } = resolvePaletteOrMuiButtonColor(color);

  const baseSx: Record<string, unknown> = {
    borderRadius: '8px',
    ...(expandHeight && { height: '100%' }),
  };

  if (resolvedColor) {
    if (variant === 'outlined') {
      baseSx.color = resolvedColor;
      baseSx.borderColor = resolvedColor;
    } else if (variant === 'contained') {
      baseSx.backgroundColor = resolvedColor;
      baseSx.color = getContrastText(resolvedColor);
    } else {
      baseSx.color = resolvedColor;
    }
  }

  const button = (
    <Button
      color={muiColor}
      variant={variant}
      startIcon={
        loading ? (
          <CircularProgress size={16} color='inherit' aria-hidden />
        ) : (
          startIcon
        )
      }
      endIcon={endIcon}
      disabled={disabled || loading}
      sx={baseSx}
      onClick={onClick}
      type={type}
      size={size}
      fullWidth={fullWidth}
    >
      {text}
    </Button>
  );

  return (
    <CustomThemeProvider initialSettings={{ themeOverride: hubThemeLight }}>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <span
            style={{
              display: fullWidth ? 'block' : 'inline-flex',
              width: fullWidth ? '100%' : undefined,
            }}
          >
            {button}
          </span>
        </Tooltip>
      ) : (
        button
      )}
    </CustomThemeProvider>
  );
};

export default MinimalButton;
